import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getJson, patchJson, postJson } from '../api/client.ts';
import { meQuery, selectableSites, sitesQuery, workersQuery } from '../api/queries.ts';
import type { MachineDetail, MachineListItem } from '../api/types.ts';
import { MachineLightBadge } from '../components/badges.tsx';
import { Button, Checkbox, EmptyState, ErrorNote, Input, PageTitle, Select, Spinner, Textarea } from '../components/ui.tsx';

/** Maschinenliste (F7) mit Suche, Standort- und Bereichsfilter. */
export function MachinesPage() {
  const search = useSearch({ from: '/app/maschinen' });
  const navigate = useNavigate({ from: '/maschinen' });
  const me = useQuery(meQuery);
  const sites = selectableSites(useQuery(sitesQuery).data, [search.siteId, search.areaId]);
  const [q, setQ] = useState(search.q ?? '');
  const role = me.data?.user.role;

  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? '') !== q) void navigate({ search: (s) => ({ ...s, q: q || undefined }) });
    }, 300);
    return () => clearTimeout(t);
  }, [q, search.q, navigate]);

  const list = useQuery({
    queryKey: ['machines', 'list', search],
    queryFn: () =>
      getJson<{ total: number; items: MachineListItem[] }>('/api/machines', {
        q: search.q,
        siteId: search.siteId,
        areaId: search.areaId,
        includeDecommissioned: search.stillgelegt,
        pageSize: 300,
      }),
  });
  const areas = sites.find((s) => s.id === search.siteId)?.areas ?? [];

  return (
    <div className="space-y-5">
      <PageTitle
        lead="Stammdaten, Dokumente und Ticketverlauf jeder Maschine."
        action={
          role && role !== 'reporter' ? (
            <div className="flex flex-wrap gap-2">
              <Link to="/maschinen/neu" className="ad-button-primary ad-button-sm">
                Maschine anlegen
              </Link>
              {role === 'lead' ? (
                <>
                  <Link to="/maschinen/etiketten" className="ad-button-outline ad-button-sm">
                    QR-Etiketten
                  </Link>
                  <Link to="/maschinen/import" className="ad-button-outline ad-button-sm">
                    CSV-Import
                  </Link>
                </>
              ) : null}
            </div>
          ) : null
        }
      >
        Maschinen
      </PageTitle>
      <div className="flex flex-wrap items-end gap-3">
        <input className="ad-input max-w-[280px]" placeholder="Name, Inventarnummer, Hersteller" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="ad-select max-w-[200px]" value={search.siteId ?? ''} onChange={(e) => void navigate({ search: (s) => ({ ...s, siteId: e.target.value || undefined, areaId: undefined }) })}>
          <option value="">Alle Werke</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        {areas.length ? (
          <select className="ad-select max-w-[220px]" value={search.areaId ?? ''} onChange={(e) => void navigate({ search: (s) => ({ ...s, areaId: e.target.value || undefined }) })}>
            <option value="">Alle Bereiche</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        ) : null}
        <Checkbox label="stillgelegte zeigen" checked={Boolean(search.stillgelegt)} onChange={(v) => void navigate({ search: (s) => ({ ...s, stillgelegt: v || undefined }) })} />
      </div>
      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorNote error={list.error} />
      ) : !list.data.items.length ? (
        <EmptyState title="Keine Maschinen" hint="Anlegen oder per CSV importieren." />
      ) : (
        <div className="overflow-x-auto border border-[var(--border-default)] bg-white">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="ad-th w-14">
                  <span className="sr-only">Foto</span>
                </th>
                <th className="ad-th">Inventar-Nr.</th>
                <th className="ad-th">Name</th>
                <th className="ad-th hidden md:table-cell">Hersteller / Typ</th>
                <th className="ad-th">Bereich</th>
                <th className="ad-th">Zustand</th>
                <th className="ad-th">Offen</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((m) => (
                <tr key={m.id} className="cursor-pointer hover:bg-steel-50" onClick={() => void navigate({ to: '/maschinen/$id', params: { id: m.id } })}>
                  <td className="ad-td w-14 py-1.5">
                    {m.photo ? (
                      <img src={m.photo.thumbUrl} alt="" loading="lazy" className="h-10 w-12 border border-[var(--border-default)] object-cover" />
                    ) : (
                      <span className="block h-10 w-12 bg-[var(--surface-subtle)]" aria-hidden />
                    )}
                  </td>
                  <td className="ad-td ad-num">{m.inventoryNo}</td>
                  <td className="ad-td font-semibold text-steel-900">
                    <Link to="/maschinen/$id" params={{ id: m.id }} className="text-steel-900 no-underline">
                      {m.name}
                    </Link>
                  </td>
                  <td className="ad-td hidden md:table-cell">{[m.manufacturer, m.model].filter(Boolean).join(', ') || '-'}</td>
                  <td className="ad-td">
                    {m.siteCode} · {m.areaName}
                  </td>
                  <td className="ad-td">
                    <MachineLightBadge light={m.light} />
                  </td>
                  <td className="ad-td ad-num">{m.openTickets || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type FormState = {
  siteId: string;
  areaId: string;
  inventoryNo: string;
  name: string;
  manufacturer: string;
  model: string;
  serialNo: string;
  yearBuilt: string;
  responsibleUserId: string;
  safetyNote: string;
  notes: string;
  inforAssetNo: string;
};

const empty: FormState = {
  siteId: '',
  areaId: '',
  inventoryNo: '',
  name: '',
  manufacturer: '',
  model: '',
  serialNo: '',
  yearBuilt: '',
  responsibleUserId: '',
  safetyNote: '',
  notes: '',
  inforAssetNo: '',
};

/** Formular für Anlegen und Bearbeiten (F8). */
export function MachineForm({ initial, onSaved, onCancel }: { initial?: MachineDetail; onSaved: (id: string) => void; onCancel?: () => void }) {
  const sites = useQuery(sitesQuery);
  const me = useQuery(meQuery);
  const [f, setF] = useState<FormState>(() =>
    initial
      ? {
          siteId: initial.site.id,
          areaId: initial.area.id,
          inventoryNo: initial.inventoryNo,
          name: initial.name,
          manufacturer: initial.manufacturer ?? '',
          model: initial.model ?? '',
          serialNo: initial.serialNo ?? '',
          yearBuilt: initial.yearBuilt ? String(initial.yearBuilt) : '',
          responsibleUserId: initial.responsible?.id ?? '',
          safetyNote: initial.safetyNote ?? '',
          notes: initial.notes ?? '',
          inforAssetNo: initial.inforAssetNo ?? '',
        }
      : { ...empty, siteId: me.data?.user.primarySiteId ?? '' },
  );
  const workers = useQuery(workersQuery(f.siteId || undefined));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof FormState) => (v: string) => setF((s) => ({ ...s, [k]: v, ...(k === 'siteId' ? { areaId: '' } : {}) }));
  // Der gespeicherte Stand bleibt wählbar: Eine Maschine zieht nicht von selbst aus einem stillgelegten Bereich aus.
  const siteOptions = selectableSites(sites.data, [f.siteId, initial?.site.id, initial?.area.id]);
  const areas = siteOptions.find((s) => s.id === f.siteId)?.areas ?? [];

  const save = async () => {
    setBusy(true);
    setError(null);
    const body = {
      siteId: f.siteId,
      areaId: f.areaId,
      inventoryNo: f.inventoryNo,
      name: f.name,
      manufacturer: f.manufacturer,
      model: f.model,
      serialNo: f.serialNo,
      yearBuilt: f.yearBuilt ? Number(f.yearBuilt) : null,
      responsibleUserId: f.responsibleUserId || null,
      safetyNote: f.safetyNote,
      notes: f.notes,
      inforAssetNo: f.inforAssetNo,
    };
    try {
      if (initial) {
        await patchJson(`/api/machines/${initial.id}`, body);
        onSaved(initial.id);
      } else {
        const res = await postJson<{ id: string }>('/api/machines', body);
        onSaved(res.id);
      }
    } catch (err) {
      setError(err);
    }
    setBusy(false);
  };

  return (
    <div className="ad-card space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Select label="Standort" required value={f.siteId} onChange={set('siteId')} options={[{ value: '', label: 'Bitte wählen' }, ...siteOptions.map((s) => ({ value: s.id, label: s.name }))]} />
        <Select label="Bereich / Halle" required value={f.areaId} onChange={set('areaId')} options={[{ value: '', label: 'Bitte wählen' }, ...areas.map((a) => ({ value: a.id, label: a.name }))]} />
        <Input label="Inventarnummer" required value={f.inventoryNo} onChange={set('inventoryNo')} />
        <Input label="Name" required value={f.name} onChange={set('name')} placeholder="z. B. Laser 2" />
        <Input label="Hersteller" value={f.manufacturer} onChange={set('manufacturer')} />
        <Input label="Typ" value={f.model} onChange={set('model')} />
        <Input label="Seriennummer" value={f.serialNo} onChange={set('serialNo')} />
        <Input label="Baujahr" inputMode="numeric" value={f.yearBuilt} onChange={set('yearBuilt')} />
        <Select
          label="Verantwortlich (bekommt P1/P2-Meldungen)"
          value={f.responsibleUserId}
          onChange={set('responsibleUserId')}
          options={[{ value: '', label: 'Niemand' }, ...(workers.data ?? []).map((w) => ({ value: w.id, label: w.displayName }))]}
        />
        <Input label="Infor-Anlagennummer" value={f.inforAssetNo} onChange={set('inforAssetNo')} />
      </div>
      <Textarea label="Sicherheitshinweis (erscheint beim Melden)" value={f.safetyNote} onChange={set('safetyNote')} rows={2} />
      <Textarea label="Notizen (nur Instandhaltung)" value={f.notes} onChange={set('notes')} rows={3} />
      {error ? <ErrorNote error={error} /> : null}
      <div className="flex gap-2">
        <Button disabled={busy || !f.siteId || !f.areaId || !f.inventoryNo || f.name.length < 2} onClick={() => void save()}>
          Speichern
        </Button>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Abbrechen
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function MachineEditPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return (
    <div className="mx-auto max-w-[820px] space-y-5">
      <PageTitle>Maschine anlegen</PageTitle>
      <MachineForm
        onSaved={(id) => {
          void qc.invalidateQueries({ queryKey: ['machines'] });
          void navigate({ to: '/maschinen/$id', params: { id } });
        }}
        onCancel={() => void navigate({ to: '/maschinen' })}
      />
    </div>
  );
}
